import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { gql, useQuery } from '@apollo/client'
import styled from 'styled-components'

import FetchMore from 'js/components/FetchMore'
import LoadingSpinner from 'js/components/LoadingSpinner/LoadingSpinner'
import Modal from 'js/components/Modal/Modal'
import SearchBar from 'js/components/SearchBar/SearchBar'
import UserLink from 'js/components/UserLink'
import { useIsMount } from 'js/lib/helpers'

const Wrapper = styled.div`
    display: flex;
    padding: 4px 0;

    &:not(:last-child) {
        border-bottom: 1px solid ${(p) => p.theme.color.grey[20]};
    }
`

interface Props {
    guid: string
    isVisible: boolean
    onClose: () => void
}

const ShowFollowersModal = ({ guid, isVisible, onClose }: Props) => {
    const { t } = useTranslation()

    const [queryLimit, setQueryLimit] = useState(50)
    const [searchInput, setSearchInput] = useState('')
    const [searchTerm, setSearchTerm] = useState('')

    const { loading, data, fetchMore, refetch } = useQuery(GET_FOLLOWERS_LIST, {
        variables: {
            query: searchTerm,
            guid,
            offset: 0,
            limit: queryLimit,
        },
    })

    const isMount = useIsMount()

    useEffect(() => {
        if (isMount) {
            refetch()
        }
    })

    useEffect(() => {
        if (isVisible) {
            refetch()
        }
    }, [isVisible, refetch])

    return (
        <Modal
            isVisible={isVisible}
            title={t('global.followers')}
            size="small"
            onClose={onClose}
            containHeight
            showCloseButton={true}
        >
            <SearchBar
                name="search-attendee"
                value={searchInput}
                onChange={(e) => {
                    setSearchInput(e.target.value)
                }}
                onSearch={() => {
                    setSearchTerm(searchInput)
                }}
                label={t('entity-event.search-attendee')}
                style={{ margin: '20px 0 8px' }}
            />

            {loading ? (
                <LoadingSpinner />
            ) : (
                //@ts-ignore
                <FetchMore
                    edges={data?.entity?.followers?.results || []}
                    getMoreResults={(data) => data.entity.followers.results}
                    fetchMore={fetchMore}
                    fetchType="scroll"
                    fetchCount={50}
                    setLimit={setQueryLimit}
                    maxLimit={data?.entity?.followers?.total}
                    isScrollableBox
                >
                    <div style={{ padding: '8px 0' }}>
                        {(data?.entity?.followers?.results || []).map(
                            (follower) => (
                                <Wrapper key={follower.guid}>
                                    <UserLink entity={follower} />
                                </Wrapper>
                            ),
                        )}
                    </div>
                </FetchMore>
            )}
        </Modal>
    )
}

const followersFragment = `
    followers(limit: $limit, q: $query) {
        total
        results {
            guid
            name
            icon
            url
        }
    }
`
const GET_FOLLOWERS_LIST = gql`
    query FollowersList($guid: String!, $limit: Int!, $query: String) {
        entity(guid: $guid) {
            guid
            ... on Blog {
                ${followersFragment}
            }
            ... on News {
                ${followersFragment}
            }
            ... on Question {
                ${followersFragment}
            }
            ... on Discussion {
                ${followersFragment}
            }
            ... on Podcast {
                ${followersFragment}
            }
            ... on GenericArticle {
                ${followersFragment}
            }
        }
    }
`

export default ShowFollowersModal
